/**
 * End-of-story flow: Quiz (4 interleaved questions) → Summary.
 *
 * Vocabulario k-*. El cierre ya no salta a otro cascarón: la historia entera
 * —leer, responder, cerrar— vive en el mismo `.k-app` de la sesión. El quiz es
 * una sesión más (línea superior + barra de segmentos + ficha en la zona viva);
 * el resumen es un informe largo, así que su columna es `.k-live`, que sí
 * scrollea, con el dock abajo para lo que sigue.
 *
 * Quiz items are generated by the story LLM at story creation and stored
 * on the Story (with a lazy backfill endpoint for legacy stories). Each
 * answer + reveal is persisted via /stories/{id}/quiz/attempts so future
 * SRS personalisation has the data.
 *
 * Summary surfaces the day's takeaway:
 *  - Hero: stats from this session (sentences pronounced, clarity %, quiz score).
 *  - Souvenir: a single sentence the user nailed today (heuristic on the
 *    in-memory scoresBySentence — best `good` count, tie-break longest).
 *  - Schedule: target words with a heuristic "next review" label
 *    (struggled-today gets bumped to próxima sesión).
 *  - Insight: LLM-generated linguistic note about the story; fetched async
 *    con una nota de carga (no un esqueleto); cached on the Story after first fetch.
 *  - NextSteps: Another story / Re-read / Home.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import type {
  ClozeQuizItem,
  GenderClozeQuizItem,
  InsightResponse,
  KlaraNoteResponse,
  L1GenderNote,
  MCQuizItem,
  QuizItem,
  ScheduleBucket,
  ScheduleEntry,
  ShadowQuizItem,
  Story,
  StoryDifficultyValue,
  StoryWord,
} from "../api/types";
import GenderPicker from "./GenderPicker";
import KlaraMark from "./KlaraMark";
import SessTop from "./SessTop";
import { onAppPause } from "../lib/native";
import { startMicRecording, type MicRecorder, type ScoreBand } from "../lib/pronunciation";
import {
  currentItem,
  initQuizSession,
  mainResults,
  quizSessionReducer,
} from "../lib/quizSession";
import { micLevelKey, startSilenceDetector, type MicLevel } from "../lib/silenceDetector";
import { speak, stop as stopTTS } from "../lib/tts";
import { useMicScorer } from "../lib/useMicScorer";

type PronScores = Record<number, ScoreBand>;

const pad2 = (n: number) => String(n).padStart(2, "0");

/* Icono de trazo del átomo 2; el tamaño lo pone `.k-rbtn svg`. */
function IcPlay(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" data-icon="play">
      <path d="M8 5.2v13.6L19 12Z" />
    </svg>
  );
}

interface Props {
  story: Story;
  scoresBySentence: Record<number, PronScores>;
  reviewIds: Set<string>;
  adding: string | null;
  onToggleReview: (word: StoryWord) => void;
  onRestart: () => void;
  onNew: () => void;
  onNextInModule?: () => void;
  onHome: () => void;
  /** Fired once per transition into the summary view (spec §4/§9: the
   * "historia completada" event fires at the Finish SUMMARY, not at quiz
   * entry). Backend is idempotent, so a StrictMode double-invoke is fine. */
  onSummaryShown?: () => void;
}

interface QuizResult {
  index: number;
  qType: "mc" | "cloze" | "shadow" | "gender_cloze";
  correct: boolean;
  revealed: boolean;
}

export default function StoryFinish({
  story,
  scoresBySentence,
  reviewIds,
  adding,
  onToggleReview,
  onRestart,
  onNew,
  onNextInModule,
  onHome,
  onSummaryShown,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [view, setView] = useState<"quiz" | "summary">("quiz");
  const [quizItems, setQuizItems] = useState<QuizItem[] | null>(null);
  const [quizError, setQuizError] = useState<boolean>(false);
  const [quizResults, setQuizResults] = useState<QuizResult[]>([]);

  // Fire once per transition into the summary view. Deliberately not
  // depending on onSummaryShown (Story.tsx passes an inline callback, so its
  // identity changes on every parent re-render) — only `view` flipping to
  // "summary" should re-fire this.
  useEffect(() => {
    if (view === "summary") onSummaryShown?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Load quiz items on mount. If the backend returns an empty list (LLM
  // gen failed for some reason), skip directly to the summary.
  useEffect(() => {
    let cancelled = false;
    api
      .getStoryQuiz(story.id)
      .then((r) => {
        if (cancelled) return;
        if (!r.items || r.items.length === 0) {
          setView("summary");
          setQuizError(true);
          return;
        }
        setQuizItems(r.items);
      })
      .catch(() => {
        if (cancelled) return;
        setQuizError(true);
        setView("summary");
      });
    return () => {
      cancelled = true;
      stopTTS();
    };
  }, [story.id]);

  if (view === "summary") {
    return (
      <Summary
        story={story}
        scoresBySentence={scoresBySentence}
        quizResults={quizResults}
        quizSkipped={quizError}
        reviewIds={reviewIds}
        adding={adding}
        onToggleReview={onToggleReview}
        onRestart={onRestart}
        onNew={onNew}
        onNextInModule={onNextInModule}
        onHome={onHome}
        onBackToQuiz={quizError ? null : () => setView("quiz")}
      />
    );
  }

  if (quizItems === null) {
    return (
      <div className="k-app">
        <SessTop titulo={story.title} right="" onExit={onHome} />
        <div className="k-fill">
          <span className="k-spinner" role="status" aria-label={t("common.loading")} />
        </div>
      </div>
    );
  }

  return (
    <Quiz
      items={quizItems}
      story={story}
      onHome={onHome}
      onComplete={(results) => {
        setQuizResults(results);
        setView("summary");
      }}
    />
  );
}

/* ============================================================
   QUIZ
   ============================================================ */

interface QuizProps {
  items: QuizItem[];
  story: Story;
  onHome: () => void;
  onComplete: (results: QuizResult[]) => void;
}

function Quiz({ items, story, onHome, onComplete }: QuizProps): JSX.Element {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(quizSessionReducer, items, initQuizSession);

  // Timing per item (patrón #115 / Repaso): shownAt resets each
  // time a new item lands on screen.
  const shownAtRef = useRef(performance.now());
  useEffect(() => {
    shownAtRef.current = performance.now();
  }, [state.phase, state.pos]);

  // Deliberately not depending on onComplete (parent passes an inline
  // callback) — only the phase flipping to "done" should fire this.
  useEffect(() => {
    if (state.phase === "done") onComplete(mainResults(state));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  const cur = currentItem(state);
  if (cur === null) return <></>;
  const { item: q, index } = cur;

  // "toSummary" only when no retry round can follow: in retry, or in main
  // with a clean sheet so far (children render the label AFTER answering,
  // when the current answer is already in state.answers).
  const failedSoFar = state.answers.some((a) => a.phase === "main" && !a.correct);
  const isLast =
    state.pos === state.order.length - 1 && (state.phase === "retry" || !failedSoFar);
  const retry = state.phase === "retry";
  const total = state.order.length;

  const onAnswered = (r: Omit<QuizResult, "index" | "qType">) => {
    const elapsedMs = Math.max(0, Math.round(performance.now() - shownAtRef.current));
    dispatch({ type: "answer", correct: r.correct, revealed: r.revealed });
    // Best-effort persistence — failures don't block the UX. Gender items
    // record their own diadic evidence (recordGenderAttempt), so skip the
    // generic quiz-attempt record for them.
    if (q.type !== "gender_cloze") {
      void api
        .recordQuizAttempt(story.id, {
          question_index: index,
          question_type: q.type,
          was_correct: r.correct,
          was_revealed: r.revealed,
          detail: { elapsed_ms: elapsedMs, phase: retry ? "retry" : "main" },
        })
        .catch(() => undefined);
    }
  };

  const onNext = () => dispatch({ type: "next" });

  return (
    <div className="k-app">
      <SessTop
        titulo={story.title}
        right={`${pad2(state.pos + 1)}/${pad2(total)}`}
        onExit={onHome}
      />
      <div className="k-bar k-bar--segs">
        <div
          className="k-bar__track"
          role="progressbar"
          aria-valuenow={state.pos + 1}
          aria-valuemin={0}
          aria-valuemax={total}
        >
          {Array.from({ length: total }, (_, i) => (
            <span
              key={i}
              className="k-bar__seg"
              data-state={i < state.pos ? "done" : i === state.pos ? "now" : undefined}
            />
          ))}
        </div>
      </div>

      <div className="k-fill">
        {/* La columna que sí scrollea: una ficha de opción múltiple con cuatro
            filas no cabe siempre en un teléfono corto. */}
        <div className="k-live">
          {/* La ronda se presenta una sola vez, al abrirla: repetirlo en cada
              pregunta robaría el alto que la ficha necesita. */}
          {state.pos === 0 && (
            <p className="k-note">
              <span className="k-note__cap">
                {retry ? t("story.finish.quiz.retry.cap") : t("story.finish.quiz.title")}
              </span>
              {retry ? t("story.finish.quiz.retry.sub") : t("story.finish.quiz.sub")}
            </p>
          )}
          <div key={`${state.phase}-${state.pos}` /* remount each q for clean state */}>
            {q.type === "mc" && (
              <MCQuestion q={q} story={story} onAnswered={onAnswered} onNext={onNext} isLast={isLast} />
            )}
            {q.type === "cloze" && (
              <ClozeQuestion q={q} story={story} onAnswered={onAnswered} onNext={onNext} isLast={isLast} />
            )}
            {q.type === "shadow" && (
              <ShadowQuestion q={q} story={story} onAnswered={onAnswered} onNext={onNext} isLast={isLast} />
            )}
            {q.type === "gender_cloze" && (
              <GenderClozeQuestion q={q} story={story} onAnswered={onAnswered} onNext={onNext} isLast={isLast} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** El botón que cierra cada ficha del quiz: siguiente pregunta o resumen.
 *  `--block` porque es el único control del pie: `.k-card__foot` reparte a los
 *  extremos y con un solo hijo lo dejaría pegado a la izquierda. */
function NextBtn({ isLast, onNext }: { isLast: boolean; onNext: () => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <button type="button" className="k-btn k-btn--block" onClick={onNext}>
      {isLast ? t("story.finish.quiz.toSummary") : t("story.finish.quiz.next")}{" "}
      <span className="k-btn__arrow" aria-hidden="true">→</span>
    </button>
  );
}

/** «Quién actúa ahora» en el pie de la ficha: el rótulo vivo toma el acento.
 *  El candidato 19 (píldora de proceso) muere aquí igual que en Hablar. */
function LiveCap({ label }: { label: string }): JSX.Element {
  return (
    <p className="k-cap" role="status" aria-live="polite">
      <span className="k-cap__count" data-tone="live">{label}</span>
    </p>
  );
}

/* ============================================================
   MC question — voice-pick (mic transcribes + fuzzy-matches the option)
   with tap as accessibility fallback.
   ============================================================ */

interface MCProps {
  q: MCQuizItem;
  story: Story;
  onAnswered: (r: Omit<QuizResult, "index" | "qType">) => void;
  onNext: () => void;
  isLast: boolean;
}

type MCMicPhase = "idle" | "recording" | "resolving" | "no_match";

function MCQuestion({ q, story, onAnswered, onNext, isLast }: MCProps): JSX.Element {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [micPhase, setMicPhase] = useState<MCMicPhase>("idle");
  const [micLevel, setMicLevel] = useState<MicLevel>("silent");
  const [lastTranscript, setLastTranscript] = useState<string>("");
  const recorderRef = useRef<MicRecorder | null>(null);
  const epochRef = useRef(0);
  const startingRef = useRef(false);
  const recAnalyserRef = useRef<AnalyserNode | null>(null);
  const answered = picked !== null;

  const cancelMic = useCallback(() => {
    epochRef.current += 1;
    recorderRef.current?.cancel();
    recorderRef.current = null;
    recAnalyserRef.current = null;
    setMicPhase("idle");
  }, []);

  const onTap = (i: number) => {
    if (answered || micPhase !== "idle") return;
    setPicked(i);
    onAnswered({ correct: i === q.correct, revealed: false });
  };
  const onReveal = () => {
    if (answered) return;
    cancelMic();
    setPicked(q.correct);
    setRevealed(true);
    onAnswered({ correct: false, revealed: true });
  };

  async function startMic() {
    if (startingRef.current || (micPhase !== "idle" && micPhase !== "no_match")) return;
    startingRef.current = true;
    const epoch = epochRef.current;
    setLastTranscript("");
    try {
      const rec = await startMicRecording();
      if (epoch !== epochRef.current) {
        rec.cancel();
        return;
      }
      recorderRef.current = rec;
      recAnalyserRef.current = rec.analyser;
      setMicPhase("recording");
    } catch {
      setMicPhase("idle");
    } finally {
      startingRef.current = false;
    }
  }

  async function stopMic() {
    const rec = recorderRef.current;
    if (rec === null || micPhase !== "recording") return;
    recorderRef.current = null;
    recAnalyserRef.current = null;
    setMicPhase("resolving");
    try {
      const blob = await rec.stop();
      if (!blob || blob.size === 0) {
        setMicPhase("no_match");
        return;
      }
      const resp = await api.resolveMC(
        story.id,
        blob,
        q.options,
        story.target_language,
      );
      setLastTranscript(resp.transcript);
      if (resp.picked_index !== null) {
        setPicked(resp.picked_index);
        onAnswered({ correct: resp.picked_index === q.correct, revealed: false });
      } else {
        setMicPhase("no_match");
      }
    } catch {
      setMicPhase("no_match");
    }
  }

  function tryAgain() {
    setMicPhase("idle");
    setLastTranscript("");
  }

  // Primary stop trigger: 1.5s of silence after the user has had ≥800ms
  // to start speaking. Falls back to a 20s hard timeout in case the
  // silence detector misbehaves (e.g. flaky mic feeding garbage samples).
  useEffect(() => {
    if (micPhase !== "recording" || !recAnalyserRef.current) return;
    setMicLevel("silent");
    const cleanupSilence = startSilenceDetector(
      recAnalyserRef.current,
      () => {
        void stopMic();
      },
      { onLevel: setMicLevel },
    );
    const safety = window.setTimeout(() => void stopMic(), 20_000);
    return () => {
      cleanupSilence();
      clearTimeout(safety);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micPhase]);

  useEffect(() => onAppPause(cancelMic), [cancelMic]);

  // Cleanup on unmount
  useEffect(() => {
    return () => cancelMic();
  }, [cancelMic]);

  return (
    <article className="k-card k-card--flat" data-type="mc">
      <header className="k-card__head">
        <span className="k-cap">{q.cap}</span>
      </header>
      <div className="k-card__body">
        <p className="k-card__word">{q.prompt}</p>
        <ul>
          {q.options.map((opt, i) => {
            // Tres cosas que decir: cuál es la buena, cuál tocaste tú y que el
            // resto ya no juega. El canto de `selected` dice «esta»; el chip
            // pone el tono (punto de color + glifo, nunca solo color).
            const isCorrect = i === q.correct;
            const isPicked = i === picked;
            let state: string | undefined;
            let tone: string | undefined;
            let mark: string | undefined;
            if (answered) {
              if (isCorrect) {
                state = "selected";
                if (revealed) mark = "→";
                else {
                  tone = "good";
                  mark = "✓";
                }
              } else if (isPicked) {
                tone = "bad";
                mark = "✗";
              } else {
                state = "locked";
              }
            }
            return (
              <li key={i}>
                <button
                  type="button"
                  className="k-row k-row--boxed"
                  data-state={state}
                  onClick={() => onTap(i)}
                  disabled={answered || micPhase === "recording" || micPhase === "resolving"}
                >
                  <span className="k-row__n">{String.fromCharCode(65 + i)}</span>
                  <span className="k-row__title">{opt}</span>
                  {mark && tone && (
                    <span className="k-chip k-chip--static" data-tone={tone}>
                      <span className="k-chip__dot" aria-hidden="true" />
                      {mark}
                    </span>
                  )}
                  {mark && !tone && <span className="k-row__meta">{mark}</span>}
                </button>
              </li>
            );
          })}
        </ul>
        {/* La prosa vive en el cuerpo y los controles en el pie: el pie reparte
            a los extremos y una nota metida ahí se estrangula a tres palabras
            por línea. */}
        {!answered && (micPhase === "idle" || micPhase === "no_match") && (
          <p className="k-note k-note--bare">
            {micPhase === "no_match"
              ? lastTranscript
                ? t("story.finish.quiz.mc.heardButNoMatch", { transcript: lastTranscript })
                : t("story.finish.quiz.mc.noSpeech")
              : t("story.finish.quiz.mc.readAloud")}
          </p>
        )}
        {answered && q.after && <p className="k-note k-note--bare">{q.after}</p>}
      </div>
      <hr className="k-card__rule" />
      <footer className="k-card__foot">
        {!answered && (micPhase === "idle" || micPhase === "no_match") && (
          <>
            <button type="button" className="k-btn k-btn--text" onClick={onReveal}>
              {t("story.finish.quiz.reveal")}
            </button>
            <button
              type="button"
              className="k-btn"
              onClick={micPhase === "no_match" ? tryAgain : () => void startMic()}
            >
              {micPhase === "no_match"
                ? t("story.finish.quiz.mc.tryAgain")
                : t("story.finish.quiz.yourTurn")}
            </button>
          </>
        )}

        {micPhase === "recording" && (
          <>
            <LiveCap label={t(micLevelKey(micLevel))} />
            <button type="button" className="k-btn k-btn--ghost" onClick={() => void stopMic()}>
              {t("story.sentence.stop")}
            </button>
          </>
        )}

        {micPhase === "resolving" && <LiveCap label={t("story.sentence.evaluating")} />}

        {answered && <NextBtn isLast={isLast} onNext={onNext} />}
      </footer>
    </article>
  );
}

/* ============================================================
   Cloze question — sentence with a blank, user speaks the word.
   ============================================================ */

interface ClozeProps {
  q: ClozeQuizItem;
  story: Story;
  onAnswered: (r: Omit<QuizResult, "index" | "qType">) => void;
  onNext: () => void;
  isLast: boolean;
}

function ClozeQuestion({
  q,
  story,
  onAnswered,
  onNext,
  isLast,
}: ClozeProps): JSX.Element {
  const { t } = useTranslation();
  const scorer = useMicScorer(q.answer, story.target_language);
  const [revealed, setRevealed] = useState(false);

  const filled = revealed || (scorer.phase === "scored" && Boolean(scorer.result));
  // Cloze "correct" threshold: Azure pronunciation score >= 60 AND the
  // recognized word matches the answer (case-insensitive, accent-relaxed).
  const isCorrect =
    scorer.result != null &&
    scorer.result.overallScore >= 60 &&
    matchesLoose(scorer.result.recognizedText, q.answer);

  // Fire the attempt record + bubble result up once scoring lands.
  const reportedRef = useRef(false);
  useEffect(() => {
    if (scorer.phase !== "scored" || !scorer.result || reportedRef.current) return;
    reportedRef.current = true;
    onAnswered({ correct: isCorrect, revealed: false });
    void api
      .recordPronunciationAttempt(story.id, {
        sentence_index: -1, // -1 marks "quiz cloze" not a story sentence
        reference_text: q.answer,
        recognized_text: scorer.result.recognizedText,
        overall_score: scorer.result.overallScore,
        word_bands: Object.fromEntries(
          Object.entries(scorer.result.bands).map(([k, v]) => [k, v]),
        ),
      })
      .catch(() => undefined);
  }, [
    isCorrect,
    onAnswered,
    q.answer,
    scorer.phase,
    scorer.result,
    story.id,
  ]);

  const onReveal = () => {
    if (filled || scorer.phase !== "idle") return;
    setRevealed(true);
    onAnswered({ correct: false, revealed: true });
  };

  const onMicClick = () => {
    if (scorer.phase === "recording") void scorer.stop();
    else if (scorer.phase === "idle" || scorer.phase === "error") void scorer.start();
  };

  return (
    <article className="k-card k-card--flat" data-type="cloze">
      <header className="k-card__head">
        <span className="k-cap">{q.cap}</span>
      </header>
      <div className="k-card__body">
        <p className="k-cap">
          <KlaraMark size={13} /> {t("story.finish.quiz.cloze.klaraSays")}
        </p>
        {/* El hueco es una palabra puntuada del pasaje (átomo 14): acertada se
            subraya en `good`, revelada en `bad`. Vacío es el `___` que ya usa
            GenderPicker; el filete dibujado de `.qcard__blank-rule` muere. */}
        <p className="k-stage">
          {q.sentence_pre}
          <span data-score={filled ? (revealed || !isCorrect ? "bad" : "good") : undefined}>
            {filled ? q.answer : "___"}
          </span>
          {q.sentence_post}
        </p>
        {q.en && <p className="k-note">{q.en}</p>}
        {scorer.phase === "idle" && !filled && q.hint && (
          <p className="k-note k-note--bare">{q.hint}</p>
        )}
        {(filled || scorer.phase === "scored") && (
          <p className="k-note k-note--bare">
            {revealed
              ? t("story.finish.quiz.cloze.wasRevealed", { answer: q.answer })
              : isCorrect
                ? t("story.finish.quiz.cloze.nailed")
                : t("story.finish.quiz.cloze.close", { answer: q.answer })}
          </p>
        )}
      </div>
      <hr className="k-card__rule" />
      <footer className="k-card__foot">
        {scorer.phase === "idle" && !filled && (
          <>
            <button type="button" className="k-btn k-btn--text" onClick={onReveal}>
              {t("story.finish.quiz.reveal")}
            </button>
            <button type="button" className="k-btn" onClick={onMicClick}>
              {t("story.finish.quiz.yourTurn")}
            </button>
          </>
        )}
        {scorer.phase === "recording" && (
          <>
            <LiveCap label={t(micLevelKey(scorer.level))} />
            <button type="button" className="k-btn k-btn--ghost" onClick={onMicClick}>
              {t("story.sentence.stop")}
            </button>
          </>
        )}
        {scorer.phase === "scoring" && <LiveCap label={t("story.sentence.evaluating")} />}
        {(filled || scorer.phase === "scored") && <NextBtn isLast={isLast} onNext={onNext} />}
      </footer>
    </article>
  );
}

/* ============================================================
   Shadow question — Klara plays, user repeats. Per-token scoring.
   ============================================================ */

interface ShadowProps {
  q: ShadowQuizItem;
  story: Story;
  onAnswered: (r: Omit<QuizResult, "index" | "qType">) => void;
  onNext: () => void;
  isLast: boolean;
}

function ShadowQuestion({
  q,
  story,
  onAnswered,
  onNext,
  isLast,
}: ShadowProps): JSX.Element {
  const { t } = useTranslation();
  const [played, setPlayed] = useState(false);
  const scorer = useMicScorer(q.sentence, story.target_language);

  // Compute tokenized rendering with per-token scoring
  const tokens = useMemo(() => tokenizeWords(q.sentence), [q.sentence]);
  const bands = scorer.result?.bands ?? null;

  const onPlay = () => {
    setPlayed(true);
    speak(q.sentence, story.target_language);
  };

  const onMicClick = () => {
    if (!played) return;
    if (scorer.phase === "recording") void scorer.stop();
    else if (scorer.phase === "idle" || scorer.phase === "error") void scorer.start();
  };

  // Persist + bubble up once scored
  const reportedRef = useRef(false);
  useEffect(() => {
    if (scorer.phase !== "scored" || !scorer.result || reportedRef.current) return;
    reportedRef.current = true;
    const goodCount = Object.values(scorer.result.bands).filter((v) => v === "good").length;
    const correct = goodCount >= Math.ceil(Object.keys(scorer.result.bands).length / 2);
    onAnswered({ correct, revealed: false });
    void api
      .recordPronunciationAttempt(story.id, {
        sentence_index: -1,
        reference_text: q.sentence,
        recognized_text: scorer.result.recognizedText,
        overall_score: scorer.result.overallScore,
        word_bands: Object.fromEntries(
          Object.entries(scorer.result.bands).map(([k, v]) => [k, v]),
        ),
      })
      .catch(() => undefined);
  }, [onAnswered, q.sentence, scorer.phase, scorer.result, story.id]);

  const verdict = useMemo(() => {
    if (!bands) return null;
    const totalBands = Object.keys(bands).length;
    if (totalBands === 0) return null;
    const good = Object.values(bands).filter((v) => v === "good").length;
    if (good === totalBands) return t("story.finish.quiz.shadow.verdict.full");
    if (good >= Math.ceil(totalBands * 0.7))
      return t("story.finish.quiz.shadow.verdict.most");
    if (good >= Math.ceil(totalBands * 0.4))
      return t("story.finish.quiz.shadow.verdict.half");
    return t("story.finish.quiz.shadow.verdict.retry");
  }, [bands, t]);

  return (
    <article className="k-card k-card--flat" data-type="shadow">
      <header className="k-card__head">
        <span className="k-cap">{q.cap}</span>
      </header>
      <div className="k-card__body">
        <p className="k-cap">
          <KlaraMark size={13} /> {t("story.finish.quiz.cloze.klaraSays")}
        </p>
        {/* Antes de oírla no hay pasaje que leer, hay una instrucción: el
            `data-played` que atenuaba la oración muere con la copia que ya lo
            dice. */}
        {played ? (
          <p className="k-stage">
            {bands
              ? tokens.map((tok, i) =>
                  tok.type === "word" ? (
                    <span key={i} data-score={bands[i]}>
                      {tok.text}
                    </span>
                  ) : (
                    <span key={i}>{tok.text}</span>
                  ),
                )
              : q.sentence}
          </p>
        ) : (
          <p className="k-note">{t("story.finish.quiz.shadow.tapToHear")}</p>
        )}
        {q.en && <p className="k-note">{q.en}</p>}
        {scorer.phase === "idle" && !bands && (
          <p className="k-note k-note--bare">
            {played
              ? t("story.finish.quiz.shadow.repeat")
              : t("story.finish.quiz.shadow.listenFirst")}
          </p>
        )}
        {scorer.phase === "scored" && bands && (
          <p className="k-note k-note--bare">
            {verdict}
            {q.after ? ` ${q.after}` : ""}
          </p>
        )}
        {scorer.phase === "scored" && bands && (
          <div className="k-wrap">
            <span className="k-chip k-chip--static" data-tone="good">
              <span className="k-chip__dot" aria-hidden="true" />
              {countBand(bands, "good")} {t("story.finish.quiz.shadow.legend.clear")}
            </span>
            <span className="k-chip k-chip--static" data-tone="ok">
              <span className="k-chip__dot" aria-hidden="true" />
              {countBand(bands, "ok")} {t("story.finish.quiz.shadow.legend.ok")}
            </span>
            <span className="k-chip k-chip--static" data-tone="bad">
              <span className="k-chip__dot" aria-hidden="true" />
              {countBand(bands, "bad")} {t("story.finish.quiz.shadow.legend.bad")}
            </span>
          </div>
        )}
      </div>
      <hr className="k-card__rule" />
      <footer className="k-card__foot">
        <button
          type="button"
          className="k-rbtn k-rbtn--ink k-rbtn--md"
          data-done={played ? "true" : undefined}
          onClick={onPlay}
          aria-label={
            played ? t("story.finish.quiz.shadow.replay") : t("story.finish.quiz.shadow.play")
          }
        >
          <IcPlay />
        </button>
        {scorer.phase === "idle" && !bands && (
          <button type="button" className="k-btn" onClick={onMicClick} disabled={!played}>
            {t("story.finish.quiz.shadow.repeatBtn")}
          </button>
        )}
        {scorer.phase === "recording" && (
          <>
            <LiveCap label={t(micLevelKey(scorer.level))} />
            <button type="button" className="k-btn k-btn--ghost" onClick={onMicClick}>
              {t("story.sentence.stop")}
            </button>
          </>
        )}
        {scorer.phase === "scoring" && <LiveCap label={t("story.sentence.evaluating")} />}
        {scorer.phase === "scored" && bands && <NextBtn isLast={isLast} onNext={onNext} />}
      </footer>
    </article>
  );
}

/* ============================================================
   Gender cloze question — 3-button tap picker (der/die/das),
   graded server-side.
   ============================================================ */

interface GenderClozeProps {
  q: GenderClozeQuizItem;
  story: Story;
  onAnswered: (r: Omit<QuizResult, "index" | "qType">) => void;
  onNext: () => void;
  isLast: boolean;
}

function GenderClozeQuestion({
  q,
  story,
  onAnswered,
  onNext,
  isLast,
}: GenderClozeProps): JSX.Element {
  return (
    <GenderPicker
      lemma={q.lemma}
      en={q.en}
      onGrade={(article) =>
        api.recordGenderAttempt(story.id, { vocab_item_id: q.vocab_item_id, picked_article: article })
      }
      onResult={(correct) => onAnswered({ correct, revealed: false })}
      onNext={onNext}
      isLast={isLast}
    />
  );
}

/* ============================================================
   SUMMARY
   ============================================================ */

function DifficultyTap({ story }: { story: Story }): JSX.Element {
  const { t } = useTranslation();
  const [value, setValue] = useState<StoryDifficultyValue | null>(
    story.perceived_difficulty ?? null,
  );
  const pick = (v: StoryDifficultyValue) => {
    setValue(v);
    // Best-effort like every other Finish signal.
    void api.setStoryDifficulty(story.id, v).catch(() => undefined);
  };
  const opts: StoryDifficultyValue[] = ["too_easy", "right", "too_hard"];
  const labels: Record<StoryDifficultyValue, string> = {
    too_easy: t("story.finish.summary.difficulty.tooEasy"),
    right: t("story.finish.summary.difficulty.right"),
    too_hard: t("story.finish.summary.difficulty.tooHard"),
  };
  return (
    <section aria-label={t("story.finish.summary.difficulty.title")}>
      <p className="k-cap">{t("story.finish.summary.difficulty.title")}</p>
      <div className="k-wrap">
        {opts.map((v) => (
          <button
            key={v}
            type="button"
            className={value === v ? "k-btn" : "k-btn k-btn--ghost"}
            aria-pressed={value === v}
            onClick={() => pick(v)}
          >
            {labels[v]}
          </button>
        ))}
      </div>
    </section>
  );
}

interface SummaryProps {
  story: Story;
  scoresBySentence: Record<number, PronScores>;
  quizResults: QuizResult[];
  quizSkipped: boolean;
  reviewIds: Set<string>;
  adding: string | null;
  onToggleReview: (word: StoryWord) => void;
  onRestart: () => void;
  onNew: () => void;
  onNextInModule?: () => void;
  onHome: () => void;
  onBackToQuiz: (() => void) | null;
}

function Summary({
  story,
  scoresBySentence,
  quizResults,
  quizSkipped,
  reviewIds,
  adding,
  onToggleReview,
  onRestart,
  onNew,
  onNextInModule,
  onHome,
  onBackToQuiz,
}: SummaryProps): JSX.Element {
  const { t } = useTranslation();
  const sentences = story.content.sentences;

  const sentencesPracticed = Object.keys(scoresBySentence).length;
  const allBands = Object.values(scoresBySentence).flatMap((s) => Object.values(s));
  const clarityPct = allBands.length
    ? Math.round((allBands.filter((v) => v === "good").length / allBands.length) * 100)
    : null;

  const quizCorrect = quizResults.filter((r) => r.correct).length;
  const quizTotal = quizResults.length;

  // Souvenir: pick the sentence with the most `good` bands; tie-break longest.
  const souvenirIdx = useMemo(() => {
    let bestIdx = 0;
    let bestGood = -1;
    let bestLen = -1;
    sentences.forEach((s, i) => {
      const bands = scoresBySentence[i];
      const good = bands ? Object.values(bands).filter((v) => v === "good").length : 0;
      const len = s.target.length;
      if (good > bestGood || (good === bestGood && len > bestLen)) {
        bestIdx = i;
        bestGood = good;
        bestLen = len;
      }
    });
    return bestIdx;
  }, [scoresBySentence, sentences]);
  const souvenir = sentences[souvenirIdx];

  // Schedule: each target word with a struggled-today tag if any sentence
  // containing the word had a `bad` band on that word's token.
  const struggled = useMemo(() => {
    const set = new Set<string>();
    const lowerLemmas = story.target_words.map((w) => ({
      id: w.id,
      lemma: w.lemma.toLowerCase(),
    }));
    sentences.forEach((s, i) => {
      const bands = scoresBySentence[i];
      if (!bands) return;
      const sentenceLower = s.target.toLowerCase();
      for (const w of lowerLemmas) {
        if (!sentenceLower.includes(w.lemma)) continue;
        // Word appears in sentence. Mark struggled only if any band in this
        // sentence is "bad" — proxy for "user had trouble with this sentence".
        if (Object.values(bands).includes("bad")) set.add(w.id);
      }
    });
    return set;
  }, [scoresBySentence, sentences, story.target_words]);

  // Insight: async fetch with a loading note
  const [insight, setInsight] = useState<InsightResponse | null>(null);
  const [insightLoading, setInsightLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api
      .getStoryInsight(story.id)
      .then((r) => {
        if (cancelled) return;
        setInsight(r);
      })
      .catch(() => {
        // best-effort: section just doesn't show
      })
      .finally(() => {
        if (!cancelled) setInsightLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [story.id]);

  // L1 gender-transfer notes (ES→DE): async, best-effort. Empty → section hidden.
  const [l1Notes, setL1Notes] = useState<L1GenderNote[]>([]);
  useEffect(() => {
    let cancelled = false;
    api
      .getStoryL1Notes(story.id)
      .then((r) => {
        if (!cancelled) setL1Notes(r.notes);
      })
      .catch(() => {
        // best-effort: section just doesn't show
      });
    return () => {
      cancelled = true;
    };
  }, [story.id]);

  // Schedule: real SRS state per target word. While loading, fall back to
  // the heuristic so the section doesn't pop in late.
  const [schedule, setSchedule] = useState<Map<string, ScheduleEntry> | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .getStorySchedule(story.id)
      .then((r) => {
        if (cancelled) return;
        setSchedule(new Map(r.entries.map((e) => [e.vocab_item_id, e])));
      })
      .catch(() => {
        // best-effort: stays on heuristic labels
      });
    return () => {
      cancelled = true;
    };
  }, [story.id]);

  // Klara Note: one-line teaser for tomorrow. Loading note while it lands;
  // nothing rendered if generation fails.
  const [klaraNote, setKlaraNote] = useState<KlaraNoteResponse | null>(null);
  const [klaraNoteLoading, setKlaraNoteLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api
      .getStoryKlaraNote(story.id)
      .then((r) => {
        if (!cancelled) setKlaraNote(r);
      })
      .catch(() => {
        // best-effort
      })
      .finally(() => {
        if (!cancelled) setKlaraNoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [story.id]);

  return (
    <div className="k-app">
      <SessTop titulo={story.title} right="" onExit={onHome} />
      <div className="k-fill">
        <div className="k-live">
          <header className="k-hero k-hero--quiet">
            {/* La cinta del héroe reparte a los extremos (lleva cifra a la
                derecha en otras pantallas): marca y rótulo van en un solo hijo
                o se separan a los dos cantos. */}
            <span className="k-hero__tag">
              <span>
                <KlaraMark size={13} /> {t("story.finish.summary.cap")}
              </span>
            </span>
            <h1 className="k-hero__title">{story.title}.</h1>
            <p className="k-hero__meta">
              <strong>{sentencesPracticed}</strong>
              {" / "}
              <strong>{sentences.length}</strong> {t("story.finish.summary.sentencesPronounced")}
              {clarityPct !== null && (
                <>
                  <span aria-hidden="true">·</span>
                  <span>
                    <strong>{clarityPct}%</strong> {t("story.finish.summary.clear")}
                  </span>
                </>
              )}
              {!quizSkipped && quizTotal > 0 && (
                <>
                  <span aria-hidden="true">·</span>
                  <span>
                    <strong>{quizCorrect}</strong>
                    {" / "}
                    <strong>{quizTotal}</strong> {t("story.finish.summary.questions")}
                  </span>
                </>
              )}
            </p>
          </header>

          <DifficultyTap story={story} />

          {souvenir && (
            <section aria-label={t("story.finish.summary.souvenir.title")}>
              <p className="k-cap">{t("story.finish.summary.souvenir.title")}</p>
              <p className="k-stage">«{souvenir.target}»</p>
              <p className="k-note">{t("story.finish.summary.souvenir.sub")}</p>
              {souvenir.native && <p className="k-note k-note--mono">{souvenir.native}</p>}
            </section>
          )}

          <hr className="k-hairline" />

          <section aria-label={t("story.finish.summary.schedule.title")}>
            <p className="k-cap">
              {t("story.finish.summary.schedule.title")}
              <span className="k-cap__count">
                {t("story.finish.summary.schedule.count", { count: story.target_words.length })}
              </span>
            </p>
            <ul>
              {story.target_words.map((w) => {
                const isStruggled = struggled.has(w.id);
                const article = story.target_language === "de" ? w.gender : null;
                const added = reviewIds.has(w.id);
                const entry = schedule?.get(w.id) ?? null;
                const label = scheduleLabel(t, entry, {
                  struggled: isStruggled,
                  justAdded: added,
                });
                return (
                  <li key={w.id} className="k-row k-row--quiet">
                    {article && (
                      <span className="k-row__n" data-gender={article}>{article}</span>
                    )}
                    <span className="k-row__title">{w.lemma}</span>
                    {isStruggled && (
                      <span className="k-chip k-chip--static" data-tone="bad">
                        <span className="k-chip__dot" aria-hidden="true" />
                        {t("story.finish.summary.schedule.struggled")}
                      </span>
                    )}
                    <span className="k-row__meta">{label}</span>
                    {w.translation && <span className="k-row__sub">{w.translation}</span>}
                    {!added && entry && !entry.has_card && (
                      <button
                        type="button"
                        className="k-btn k-btn--text"
                        onClick={() => onToggleReview(w)}
                        disabled={adding === w.id}
                      >
                        {adding === w.id
                          ? t("story.end.words.adding")
                          : t("story.end.words.add")}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>

          <hr className="k-hairline" />

          <section aria-label={t("story.finish.summary.insight.title")}>
            <p className="k-cap">{t("story.finish.summary.insight.title")}</p>
            {insightLoading ? (
              <p className="k-note">{t("common.loading")}</p>
            ) : insight ? (
              <div className="k-note">
                <p className="k-note__body">
                  <strong>{insight.title}</strong>
                </p>
                <p className="k-note__body">{insight.body}</p>
              </div>
            ) : (
              <p className="k-note">{t("story.finish.summary.insight.unavailable")}</p>
            )}
          </section>

          {l1Notes.length > 0 && (
            <>
              <hr className="k-hairline" />
              <section aria-label={t("story.finish.summary.l1Notes.title")}>
                <p className="k-cap">{t("story.finish.summary.l1Notes.title")}</p>
                <p className="k-note">{t("story.finish.summary.l1Notes.hint")}</p>
                <ul>
                  {l1Notes.map((n) => (
                    <li key={n.lemma} className="k-row k-row--quiet">
                      <span className="k-row__n" data-gender={n.gender}>{n.gender}</span>
                      <span className="k-row__title">{n.lemma}</span>
                      <span className="k-row__sub">{n.note}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}

          {(klaraNoteLoading || klaraNote) && (
            <>
              <hr className="k-hairline" />
              <section aria-label={t("story.finish.summary.note.aria")}>
                {klaraNoteLoading ? (
                  <p className="k-note">{t("common.loading")}</p>
                ) : klaraNote ? (
                  <>
                    <p className="k-note">«{klaraNote.body}»</p>
                    {/* La firma es la marca, no una «K» de texto (átomo 16). */}
                    <p className="k-note k-note--bare">
                      <span aria-hidden="true">— </span>
                      <KlaraMark size={15} />
                    </p>
                  </>
                ) : null}
              </section>
            </>
          )}
        </div>
      </div>

      <div className="k-dock">
        <div className="k-wrap">
          {onNextInModule && (
            <button type="button" className="k-btn k-btn--lg k-btn--block" onClick={onNextInModule}>
              {t("story.end.cta.nextInModule")}{" "}
              <span className="k-btn__arrow" aria-hidden="true">→</span>
            </button>
          )}
          <button
            type="button"
            className={
              onNextInModule ? "k-btn k-btn--ghost k-btn--block" : "k-btn k-btn--lg k-btn--block"
            }
            onClick={onNew}
          >
            {t("story.end.cta.another")}{" "}
            <span className="k-btn__arrow" aria-hidden="true">→</span>
          </button>
          <button type="button" className="k-btn k-btn--ghost k-btn--block" onClick={onRestart}>
            {t("story.end.cta.reread")}
          </button>
        </div>
        {onBackToQuiz && (
          <div className="k-wrap">
            <button type="button" className="k-btn k-btn--text" onClick={onBackToQuiz}>
              {t("story.finish.summary.backToQuiz")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   Helpers
   ============================================================ */

interface Tok {
  type: "word" | "space" | "punct";
  text: string;
}

function tokenizeWords(text: string): Tok[] {
  const re = /(\s+)|([.,!?;:„“”»«()¡¿—–\-]+)|([^\s.,!?;:„“”»«()¡¿—–\-]+)/g;
  const out: Tok[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m[1]) out.push({ type: "space", text: m[1] });
    else if (m[2]) out.push({ type: "punct", text: m[2] });
    else if (m[3]) out.push({ type: "word", text: m[3] });
  }
  return out;
}

function countBand(bands: Record<number, ScoreBand>, band: ScoreBand): number {
  return Object.values(bands).filter((v) => v === band).length;
}

/**
 * Render the SRS schedule label for one word.
 *
 * Priority (overrides cascade down):
 *   1. Just-added in this session   → "Añadida"
 *   2. Schedule data not yet loaded → fall back to the in-session heuristic
 *      (struggled → "Próxima sesión" else "Pendiente")
 *   3. No SRS card at all           → "+ Añadir a repaso" CTA caller renders
 *   4. Real bucket from the backend → localised label per bucket
 */
function scheduleLabel(
  t: (key: string) => string,
  entry: ScheduleEntry | null,
  ctx: { struggled: boolean; justAdded: boolean },
): string {
  if (ctx.justAdded) return t("story.finish.summary.schedule.added");
  if (entry === null) {
    return ctx.struggled
      ? t("story.finish.summary.schedule.nextSession")
      : t("story.finish.summary.schedule.later");
  }
  if (!entry.has_card) {
    return t("story.finish.summary.schedule.notInSrs");
  }
  const map: Record<ScheduleBucket, string> = {
    not_in_srs: t("story.finish.summary.schedule.notInSrs"),
    due_now: t("story.finish.summary.schedule.dueNow"),
    soon: t("story.finish.summary.schedule.soon"),
    this_week: t("story.finish.summary.schedule.thisWeek"),
    next_week: t("story.finish.summary.schedule.nextWeek"),
    later: t("story.finish.summary.schedule.later"),
  };
  return map[entry.bucket];
}

/** Case + diacritic insensitive equality. "AUTOBÚS" === "autobus". */
function matchesLoose(a: string, b: string): boolean {
  const normalize = (s: string) =>
    s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
  return normalize(a).includes(normalize(b)) || normalize(b).includes(normalize(a));
}
